import React from 'react';
import { Box, ButtonBase, Skeleton, Tooltip, Typography } from '@mui/material';
import { formatHours } from '../monthly/format';
import { cardSx, cardTitleSx, DAY_COLORS, GREEN, INK, MUTED } from './homeTheme';

const WEEK = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const CELL = {
  ok: { bg: 'var(--rt-cal-ok)', fg: INK },
  partial: { bg: 'var(--rt-cal-partial)', fg: 'var(--rt-amber)' },
  under: { bg: 'var(--rt-cal-under)', fg: 'var(--rt-warn)' },
  missing: { bg: 'var(--rt-cal-missing)', fg: 'var(--rt-danger-strong)' },
  absence: { bg: 'var(--rt-cal-absence)', fg: 'var(--rt-danger-strong)' },
  weekend: { bg: 'var(--rt-cal-weekend)', fg: 'var(--rt-faint)' },
  today: { bg: 'var(--rt-paper)', fg: INK },
  future: { bg: 'var(--rt-paper)', fg: MUTED },
};

function tooltipFor(d) {
  const parts = [];
  if (d.absence) parts.push(d.absence.full_day ? 'Full-day absence' : 'Partial absence');
  if (d.future) {
    if (d.forecast) parts.push(`${formatHours(d.forecast)}h forecast`);
  } else parts.push(`${formatHours(d.actual)}h tracked${d.target ? ` of ${formatHours(d.target)}h` : ''}`);
  return parts.join(' · ');
}

/** Calendario del mese: ogni giorno con le ore e il colore del suo stato; clic → Daily Tracking. */
export default function MonthCalendar({ stats, onOpenDay, title = 'Calendar', selectedKey }) {
  const days = stats.days || [];
  const offset = days.length ? (days[0].label === 'Su' ? 6 : WEEK.indexOf(days[0].label)) : 0;

  return (
    <Box sx={cardSx}>
      <Typography sx={{ ...cardTitleSx, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.75 }}>
        {WEEK.map((w) => (
          <Typography key={w} sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, textAlign: 'center', pb: 0.25 }}>
            {w}
          </Typography>
        ))}
        {Array.from({ length: offset }).map((_, i) => (
          <Box key={`e${i}`} />
        ))}
        {!stats.ready
          ? Array.from({ length: 30 }).map((_, i) => <Skeleton key={i} variant="rounded" height={48} />)
          : days.map((d) => {
              const c = CELL[d.status];
              const hours = d.future ? d.forecast : d.actual;
              return (
                <Tooltip key={d.day} title={tooltipFor(d)} arrow>
                  <ButtonBase
                    onClick={() => onOpenDay(d.dateKey)}
                    sx={{
                      height: 48,
                      borderRadius: 2,
                      bgcolor: c.bg,
                      flexDirection: 'column',
                      gap: 0.25,
                      border: d.dateKey === selectedKey ? `2px solid ${GREEN}` : d.today ? `2px solid ${INK}` : '1px solid transparent',
                      transition: 'transform 0.12s ease',
                      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 10px var(--rt-hairline)' },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: d.today ? 800 : 600, color: d.status === 'weekend' ? 'var(--rt-faint)' : INK, lineHeight: 1 }}>
                      {d.day}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        lineHeight: 1,
                        color: d.future && hours ? DAY_COLORS.future : c.fg,
                        fontStyle: d.future && hours ? 'italic' : 'normal',
                      }}
                    >
                      {d.status === 'absence' && !hours ? 'OFF' : d.status === 'missing' ? '0h' : hours ? `${formatHours(hours)}h` : ''}
                    </Typography>
                  </ButtonBase>
                </Tooltip>
              );
            })}
      </Box>
    </Box>
  );
}
